/* Mondō About — reversible instant-print sequence. */
(function () {
  'use strict';

  var controllers = new Map();
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var shortLandscape = window.matchMedia('(max-height: 600px) and (orientation: landscape)');
  var mobileViewport = window.matchMedia('(max-width: 640px)');
  var FIRST_PRINT = 0.01;
  var PRINT_STEP = 0.19;
  var PRINT_DURATION = 0.23;
  var EJECTION_SHARE = 0.62;

  function clamp(value) {
    return Math.max(0, Math.min(1, value));
  }

  function smoothstep(value) {
    var progress = clamp(value);
    return progress * progress * (3 - (2 * progress));
  }

  function phaseFor(progress, index) {
    var start = FIRST_PRINT + (index * PRINT_STEP);
    if (progress >= start + PRINT_DURATION - 0.0001) return 1;
    return clamp((progress - start) / PRINT_DURATION);
  }

  function motionIsDisabled() {
    return reduceMotion.matches || shortLandscape.matches;
  }

  function isMobileSafari() {
    var browser = window.navigator || {};
    var userAgent = browser.userAgent || '';
    return mobileViewport.matches &&
      browser.vendor === 'Apple Computer, Inc.' &&
      /Safari\//.test(userAgent) &&
      !/(CriOS|FxiOS|EdgiOS|OPiOS)/.test(userAgent);
  }

  function webkitTimelineIsAvailable() {
    return !!(window.CSS && typeof window.CSS.supports === 'function' &&
      window.CSS.supports('animation-timeline', 'view()'));
  }

  function stateFor(phase) {
    if (phase <= 0) return 'hidden';
    if (phase >= 1) return 'settled';
    return 'ejecting';
  }

  function setPrintState(print, state) {
    if (state === 'hidden') {
      print.classList.remove('is-ejecting');
      print.classList.remove('is-settled');
    } else if (state === 'settled') {
      print.classList.remove('is-ejecting');
      print.classList.add('is-settled');
    } else {
      print.classList.remove('is-settled');
      print.classList.add('is-ejecting');
    }
  }

  function cssNumber(element, property, fallback) {
    var value = parseFloat(window.getComputedStyle(element).getPropertyValue(property));
    return Number.isFinite(value) ? value : fallback;
  }

  function mount(root) {
    if (!root || controllers.has(root)) return;

    var sticky = root.querySelector('.about-photo-eject__sticky');
    var prints = Array.prototype.slice.call(root.querySelectorAll('[data-about-photo-print]'));
    if (!sticky || !prints.length) return;
    var washes = prints.map(function (print) {
      return print.querySelector('.about-photo-eject__wash');
    });

    var frame = null;
    var observer = null;
    var resizeObserver = null;
    var stacks = [];
    var previousPhases = prints.map(function () { return null; });
    var previousStates = prints.map(function () { return null; });
    var nearby = true;
    var tracking = false;
    var webkitTracking = false;
    var sequenceComplete = false;
    var completionScroll = Infinity;
    var destroyed = false;
    var progressTop = 58;
    var travel = 1;

    function measure() {
      var resolvedTop = parseFloat(window.getComputedStyle(sticky).top);
      progressTop = Number.isFinite(resolvedTop) ? resolvedTop : 58;
      travel = Math.max(1, root.offsetHeight - sticky.offsetHeight);
    }

    function readStacks() {
      stacks = prints.map(function (print) {
        return {
          x: cssNumber(print, '--about-stack-x', 0),
          y: cssNumber(print, '--about-stack-y', 96),
          turn: cssNumber(print, '--about-stack-turn', 0)
        };
      });
      previousPhases = prints.map(function () { return null; });
    }

    function currentScrollY() {
      if (Number.isFinite(window.scrollY)) return window.scrollY;
      return Number.isFinite(window.pageYOffset) ? window.pageYOffset : 0;
    }

    function readProgress() {
      var rootTop = root.getBoundingClientRect().top;
      completionScroll = currentScrollY() + rootTop + travel - progressTop;
      return clamp((progressTop - rootTop) / travel);
    }

    function measureCompletionScroll() {
      completionScroll = currentScrollY() + root.getBoundingClientRect().top + travel - progressTop;
    }

    function writeMotion(print, washLayer, x, y, yUnit, turn, wash) {
      print.style.transform = 'translate(-50%,' + y.toFixed(3) + yUnit + ') translateX(' +
        x.toFixed(3) + 'px) rotate(' + turn.toFixed(3) + 'deg)';
      if (washLayer) washLayer.style.opacity = wash.toFixed(3);
    }

    function renderPrint(print, washLayer, stack, phase) {
      var x = 0;
      var y = -102;
      var yUnit = '%';
      var turn = 0;
      var washProgress = smoothstep(clamp(phase / 0.78));
      var wash = 0.94 * (1 - washProgress);

      if (phase <= EJECTION_SHARE) {
        var ejection = smoothstep(phase / EJECTION_SHARE);
        y = -102 + (102 * ejection);
      } else {
        var fall = smoothstep((phase - EJECTION_SHARE) / (1 - EJECTION_SHARE));
        x = stack.x * fall;
        y = stack.y * fall;
        yUnit = 'px';
        turn = stack.turn * fall;
      }

      writeMotion(print, washLayer, x, y, yUnit, turn, wash);
    }

    function paint() {
      frame = null;
      if (destroyed || !tracking || !nearby || motionIsDisabled()) return;

      var progress = readProgress();
      sequenceComplete = progress >= 1 - 0.0001;
      prints.forEach(function (print, index) {
        var phase = phaseFor(progress, index);
        if (phase === previousPhases[index]) return;
        var state = stateFor(phase);
        if (state !== previousStates[index]) {
          setPrintState(print, state);
          previousStates[index] = state;
        }
        renderPrint(print, washes[index], stacks[index], phase);
        previousPhases[index] = phase;
      });
    }

    function requestPaint() {
      if (frame === null && !destroyed && tracking) frame = window.requestAnimationFrame(paint);
    }

    function onScroll() {
      if (!tracking || destroyed || !nearby) return;
      if (sequenceComplete && currentScrollY() >= completionScroll - 0.5) return;
      requestPaint();
    }

    function onResize() {
      if (!tracking || destroyed) return;
      readStacks();
      measure();
      requestPaint();
    }

    function attachTracking() {
      if (tracking || destroyed) return;
      tracking = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize, { passive: true });

      if ('IntersectionObserver' in window) {
        var nextObserver = new IntersectionObserver(function (entries) {
          if (!tracking || destroyed || observer !== nextObserver) return;
          nearby = entries.some(function (entry) { return entry.isIntersecting; });
          if (nearby) root.classList.add('is-nearby');
          else root.classList.remove('is-nearby');
          if (nearby) requestPaint();
        }, { rootMargin: '0px 0px 75% 0px' });
        observer = nextObserver;
        observer.observe(root);
      }

      if ('ResizeObserver' in window) {
        var nextResizeObserver = new ResizeObserver(function () {
          if (!tracking || destroyed || resizeObserver !== nextResizeObserver) return;
          readStacks();
          measure();
          requestPaint();
        });
        resizeObserver = nextResizeObserver;
        resizeObserver.observe(root);
      }
    }

    function detachTracking() {
      if (!tracking) return;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (observer) observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      observer = null;
      resizeObserver = null;
      tracking = false;
    }

    function setWebKitState(complete) {
      sequenceComplete = complete;
      if (complete) {
        root.classList.add('is-webkit-complete');
        root.classList.remove('is-webkit-native');
      } else {
        root.classList.add('is-webkit-native');
        root.classList.remove('is-webkit-complete');
      }
    }

    function onWebKitScroll() {
      var complete = currentScrollY() >= completionScroll - 0.5;
      if (complete !== sequenceComplete) setWebKitState(complete);
    }

    function attachWebKitTracking() {
      if (webkitTracking || destroyed) return;
      webkitTracking = true;
      window.addEventListener('scroll', onWebKitScroll, { passive: true });
    }

    function detachWebKitTracking() {
      if (!webkitTracking) return;
      window.removeEventListener('scroll', onWebKitScroll);
      webkitTracking = false;
    }

    function clearInlineMotion(print, washLayer) {
      print.style.removeProperty('transform');
      if (washLayer) washLayer.style.removeProperty('opacity');
    }

    function showStaticStack() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      detachTracking();
      detachWebKitTracking();
      root.classList.remove('is-webkit-native');
      root.classList.remove('is-webkit-complete');
      root.classList.remove('is-motion-ready');
      root.classList.remove('is-nearby');
      sequenceComplete = false;
      completionScroll = Infinity;
      previousPhases = prints.map(function () { return null; });
      previousStates = prints.map(function () { return 'settled'; });
      prints.forEach(function (print, index) {
        clearInlineMotion(print, washes[index]);
        print.classList.remove('is-ejecting');
        print.classList.add('is-settled');
      });
    }

    function prepareWebKitTimeline() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      detachTracking();
      nearby = false;
      root.classList.remove('is-nearby');
      root.classList.remove('is-webkit-complete');
      root.classList.add('is-motion-ready');
      sequenceComplete = false;
      completionScroll = Infinity;
      previousPhases = prints.map(function () { return null; });
      previousStates = prints.map(function () { return null; });
      prints.forEach(function (print, index) {
        clearInlineMotion(print, washes[index]);
        print.classList.remove('is-ejecting');
        print.classList.remove('is-settled');
      });
      measure();
      measureCompletionScroll();
      attachWebKitTracking();
      setWebKitState(currentScrollY() >= completionScroll - 0.5);
    }

    function prepareMotion() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      detachWebKitTracking();
      nearby = true;
      root.classList.remove('is-webkit-native');
      root.classList.remove('is-webkit-complete');
      root.classList.add('is-motion-ready');
      root.classList.add('is-nearby');
      sequenceComplete = false;
      completionScroll = Infinity;
      previousStates = prints.map(function () { return null; });
      readStacks();
      measure();
      attachTracking();
      requestPaint();
    }

    function syncMode() {
      var safari = isMobileSafari();
      if (motionIsDisabled() || (safari && !webkitTimelineIsAvailable())) showStaticStack();
      else if (safari) prepareWebKitTimeline();
      else prepareMotion();
    }

    reduceMotion.addEventListener('change', syncMode);
    shortLandscape.addEventListener('change', syncMode);
    mobileViewport.addEventListener('change', syncMode);

    controllers.set(root, function () {
      destroyed = true;
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      detachTracking();
      detachWebKitTracking();
      reduceMotion.removeEventListener('change', syncMode);
      shortLandscape.removeEventListener('change', syncMode);
      mobileViewport.removeEventListener('change', syncMode);
      root.classList.remove('is-webkit-native');
      root.classList.remove('is-webkit-complete');
      root.classList.remove('is-motion-ready');
      root.classList.remove('is-nearby');
      prints.forEach(function (print, index) {
        clearInlineMotion(print, washes[index]);
        print.classList.remove('is-ejecting');
        print.classList.remove('is-settled');
      });
      controllers.delete(root);
    });

    syncMode();
  }

  function mountWithin(scope) {
    var roots = [];
    if (scope.matches && scope.matches('[data-about-photo-eject]')) roots.push(scope);
    if (scope.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(scope.querySelectorAll('[data-about-photo-eject]')));
    }
    roots.forEach(mount);
  }

  function unmountWithin(scope) {
    controllers.forEach(function (destroy, root) {
      if (root === scope || (scope.contains && scope.contains(root))) destroy();
    });
  }

  mountWithin(document);
  document.addEventListener('shopify:section:load', function (event) { mountWithin(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { unmountWithin(event.target); });
})();
